/*  ========================================================================
    # Components - Lazy Image
    ========================================================================  */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import LazySource from './LazySource';
import clsx from 'clsx';

const LazyImage = ({
  figureClass,
  imageClass,
  children,
  defaultUrl,
  placeholderUrl,
  alt,
  caption = false,
  onClick
}) => {
  const imageElmRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const defaultPlaceholder = placeholderUrl
    ? placeholderUrl
    : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPs+Q8AAh0BjeU/5JAAAAAASUVORK5CYII=';

  /**
   * Source elements.
   */
  const sourceElements = (items, isVisible) => {
    if (!items) {
      return null;
    } else {
      return Array.isArray(items)
        ? items.map((child, index) => React.cloneElement(child, { key: index, visible: isVisible }))
        : React.cloneElement(items, { visible: isVisible });
    }
  };

  /**
   * Intersection Observer.
   */
  const { ref, inView } = useInView({
    rootMargin: '300px',
    threshold: 0,
    triggerOnce: true
  });

  /**
   * Image loaded.
   */
  useEffect(() => {
    imageElmRef.current?.addEventListener('load', setImageLoaded(true));
    return () => imageElmRef.current?.removeEventListener('load', setImageLoaded(true));
  }, [imageElmRef.current]);

  return (
    <figure
      ref={ref}
      onClick={onClick}
      style={{ backgroundImage: `url(${defaultPlaceholder})` }}
      className={clsx(
        'c-lazy-image',
        inView && imageLoaded ? '' : 'c-lazy-image--pending',
        !inView && !imageLoaded && !placeholderUrl ? 'c-lazy-image--loading' : '',
        figureClass
      )}
    >
      <div className="c-lazy-image__loader" />
      <picture>
        {sourceElements(children, inView)}
        <img
          className={['c-lazy-image__img', imageClass].filter(Boolean).join(' ')}
          src={inView && imageLoaded ? defaultUrl : defaultPlaceholder}
          alt={alt}
          ref={imageElmRef}
        />
      </picture>
      {caption ? <div className="u-image-caption">{caption}</div> : ''}
    </figure>
  );
};

LazyImage.propTypes = {
  figureClass: PropTypes.string,
  imageClass: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.objectOf(LazySource), PropTypes.arrayOf(LazySource)]),
  defaultUrl: PropTypes.string,
  placeholderUrl: PropTypes.string,
  alt: PropTypes.string,
  onClick: PropTypes.func
};

export default LazyImage;
